import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { login } from '../store/authSlice';
import axiosInstance from '../axiosInstance';
import { setAuthToken } from '../auth/authUtils';
import { useTranslation } from 'react-i18next';

/**
 * Shared forgot-password form hook — all 3-step logic in one place.
 * Every forgot-password theme variant uses this hook for identical behaviour.
 */
const useForgotPasswordForm = () => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const codeInputRefs = useRef([]);

  // Timer for resend code
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first code input when step 2
  useEffect(() => {
    if (step === 2 && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [step]);

  const handleEmailChange = (value) => {
    setEmail(value);
    setError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/forgot-password', { email });
      if (response.data.success) {
        setStep(2);
        setResendTimer(60);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('forgotPassword.failedSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    if (pastedData.length === 6) {
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    const fullCode = code.join('');

    if (fullCode.length !== 6) {
      setError(t('forgotPassword.enterCode'));
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/verify-reset-code', {
        email,
        code: fullCode,
      });
      if (response.data.success) {
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('forgotPassword.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    setError('');
  };

  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    setError('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('forgotPassword.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('forgotPassword.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/reset-password', {
        email,
        code: code.join(''),
        password,
        password_confirmation: confirmPassword,
      });

      if (response.data.success) {
        const { user, token } = response.data.data;
        setAuthToken(token, false);
        dispatch(login({ user, token }));
        // Return-destination priority: ?returnTo, location.state.from
        // (carried through from AuthWrapper when the user was bounced
        // off a protected page → /login → here), then /home as fallback.
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');
        const fromState = location.state?.from?.pathname
          ? location.state.from.pathname + (location.state.from.search || '')
          : null;
        navigate(returnTo || fromState || '/home', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || t('forgotPassword.failedResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/forgot-password', { email });
      if (response.data.success) {
        setResendTimer(60);
        setCode(['', '', '', '', '', '']);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('forgotPassword.failedResendCode'));
    } finally {
      setLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep(1);
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  return {
    // State
    step,
    email,
    code,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    error,
    resendTimer,
    codeInputRefs,
    // Setters
    setShowPassword,
    setShowConfirmPassword,
    // Handlers
    handleEmailChange,
    handleSendCode,
    handleCodeChange,
    handleCodeKeyDown,
    handleCodePaste,
    handleVerifyCode,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleResetPassword,
    handleResendCode,
    goBackToEmail,
  };
};

export default useForgotPasswordForm;
